// API Service for making requests to Rails API
class ApiService {
    baseURL: string;
    headers: Record<string, string>;

    constructor() {
        this.baseURL = '/api/v1';
        this.headers = {
            'Content-Type': 'application/json',
            'X-CSRF-Token': (window as any).CSRF_TOKEN || ''
        };
    }

    async request(endpoint: string, options: RequestInit = {}): Promise<any> {
        const url = `${this.baseURL}${endpoint}`;

        // Ensure we always have the latest CSRF token
        const csrfToken = (window as any).CSRF_TOKEN ||
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
            '';

        const isFormData = options.body instanceof FormData;

        const config: RequestInit = {
            ...options,
            headers: {
                ...this.headers,
                'X-CSRF-Token': csrfToken,
                'X-Firm-ID': localStorage.getItem('current_firm_id') || '',
                ...((options.headers || {}) as Record<string, string>)
            }
        };

        if (isFormData) {
            delete (config.headers as Record<string, string>)['Content-Type'];
        }

        try {
            const response = await fetch(url, config);

            // Check if response is empty
            const text = await response.text();

            // Detect HTML responses (usually error pages)
            if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
                console.error('Received HTML instead of JSON:', text.substring(0, 100));
                return {
                    success: false,
                    message: `Server Error (${response.status}). Please check logs or contact support.`,
                    errors: null
                };
            }

            const data = text ? JSON.parse(text) : {};

            if (!response.ok) {
                // Success for 4xx/5xx still returns JSON usually
                return {
                    success: false,
                    message: data.message || data.error || `Error ${response.status}`,
                    errors: data.errors || null
                };
            }

            // Standardize response format if not already
            if (data.success !== undefined) return data;

            return {
                success: true,
                data: data
            };
        } catch (error: any) {
            console.error('API Error:', error);
            return {
                success: false,
                message: error.message || 'Network error'
            };
        }
    }

    // Generic Methods
    get(endpoint: string, params: Record<string, any> = {}) {
        let url = endpoint;
        const queryParams = new URLSearchParams(params).toString();

        if (queryParams) {
            url += (url.includes('?') ? '&' : '?') + queryParams;
        }

        return this.request(url, { method: 'GET' });
    }

    post(endpoint: string, data: any = {}, options: RequestInit = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: data instanceof FormData ? data : JSON.stringify(data),
            ...options
        });
    }

    put(endpoint: string, data: any = {}, options: RequestInit = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: data instanceof FormData ? data : JSON.stringify(data),
            ...options
        });
    }

    patch(endpoint: string, data: any = {}, options: RequestInit = {}) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: data instanceof FormData ? data : JSON.stringify(data),
            ...options
        });
    }

    delete(endpoint: string) {
        return this.request(endpoint, {
            method: 'DELETE'
        });
    }

    // Firms
    getFirms(params: any = {}) {
        return this.get('/firms', params);
    }

    // Entity Specific Helpers (keeping them for backward compatibility if used)
    getProducts(params: any = {}) {
        return this.get('/products', params);
    }

    getProduct(id: string | number) {
        return this.get(`/products/${id}`);
    }

    createProduct(data: any) {
        return this.post('/products', { product: data });
    }

    updateProduct(id: string | number, data: any) {
        return this.put(`/products/${id}`, { product: data });
    }

    deleteProduct(id: string | number) {
        return this.delete(`/products/${id}`);
    }

    // Categories
    getCategories(params: any = {}) {
        return this.get('/categories', params);
    }

    createCategory(data: any) {
        return this.post('/categories', { category: data });
    }

    // Customers
    getCustomers(params: any = {}) {
        return this.get('/customers', params);
    }

    createCustomer(data: any) {
        return this.post('/customers', { customer: data });
    }

    deleteCustomer(id: string | number) {
        return this.delete(`/customers/${id}`);
    }

    // Orders
    getOrders(params: any = {}) {
        return this.get('/orders', params);
    }

    createOrder(data: any) {
        return this.post('/orders', { order: data });
    }

    // Invoices
    getInvoices(params: any = {}) {
        return this.get('/invoices', params);
    }

    // Purchases
    getPurchases(params: any = {}) {
        return this.get('/purchases', params);
    }

    // Restocks
    getRestocks(params: any = {}) {
        return this.get('/restocks', params);
    }

    // Return Orders
    getReturnOrders(params: any = {}) {
        return this.get('/return_orders', params);
    }

    // Credit Notes
    getCreditNotes(params: any = {}) {
        return this.get('/credit_notes', params);
    }

    // Product Units
    getProductUnits(params: any = {}) {
        return this.get('/product_units', params);
    }

    // Stock Adjustments
    getStockAdjustments(params: any = {}) {
        return this.get('/stock_adjustments', params);
    }

    // Settings
    getFinanceCompanies(params: any = {}) {
        return this.get('/finance_companies', params);
    }

    getInsuranceCompanies(params: any = {}) {
        return this.get('/insurance_companies', params);
    }

    getExchangeDealers(params: any = {}) {
        return this.get('/exchange_dealers', params);
    }

    getLocations(params: any = {}) {
        return this.get('/locations', params);
    }

    getRtoAgents(params: any = {}) {
        return this.get('/rto_agents', params);
    }

    getRoles(params: any = {}) {
        return this.get('/settings/roles', params);
    }

    getStaff(params: any = {}) {
        return this.get('/settings/staff', params);
    }

    // Accounting
    getAccountHeads(params: any = {}) {
        return this.get('/account_heads', params);
    }

    getAccountGroups(params: any = {}) {
        return this.get('/account_groups', params);
    }

    getVoucherTypes(params: any = {}) {
        return this.get('/voucher_types', params);
    }

    getVouchers(params: any = {}) {
        return this.get('/vouchers', params);
    }

    // Dashboard
    getDashboard() {
        return this.get('/dashboard');
    }

    // Portal (Customer)
    getPortalDashboard() {
        return this.get('/portal/dashboard');
    }

    getPortalOrders(params: any = {}) {
        return this.get('/portal/orders', params);
    }

    getPortalOrder(id: string | number) {
        return this.get(`/portal/orders/${id}`);
    }

    getPortalDeliveries(params: any = {}) {
        return this.get('/portal/deliveries', params);
    }

    getPortalSubscriptions() {
        return this.get('/portal/deliveries/subscriptions');
    }

    getPortalInvoices(params: any = {}) {
        return this.get('/portal/invoices', params);
    }

    getPortalInvoice(id: string | number) {
        return this.get(`/portal/invoices/${id}`);
    }

    // Staff (Field/Mobile)
    getStaffDashboard() {
        return this.get('/staff/dashboard');
    }

    getStaffDeliveries(params: any = {}) {
        return this.get('/staff/deliveries', params);
    }

    updateStaffDelivery(id: string | number, data: any) {
        return this.put(`/staff/deliveries/${id}`, { delivery: data });
    }
}

export default new ApiService();
